import { makePath } from '@ck';
import { AdaptiveColor, Color } from '@color';
import { BezierCurve, Ellipse, LineSegment, Path, type Point, rect } from '@geom';
import type { Path as CKPath } from 'canvaskit';
import { PaintStyle, type Visual, withVisualPaint } from './Visual';

export enum MarkerShape {
  CIRCLE = 'CIRCLE',
  SQUARE = 'SQUARE'
}

export function drawMarker(
  visual: Visual,
  p: Point,
  opts?: {
    radius?: number;
    shape?: MarkerShape;
    fillColor?: Color;
    strokeColor?: Color;
  }
) {
  const radius = opts?.radius ?? 5;
  const shape = opts?.shape ?? MarkerShape.CIRCLE;
  let fillColor = opts?.fillColor;
  const strokeColor = opts?.strokeColor;

  if (!fillColor && !strokeColor) {
    fillColor = AdaptiveColor.RED.realize(visual.colorMode);
  }

  if (fillColor) {
    switch (shape) {
      case MarkerShape.CIRCLE:
        withVisualPaint(paint => {
          paint.style = PaintStyle.FILL;
          paint.color = fillColor;
          visual.drawCircle(p, radius, paint);
        });
        break;
      case MarkerShape.SQUARE:
        visual.drawRect(rect(p.x - radius, p.y - radius, p.x + radius, p.y + radius), {
          fill: fillColor
        });
        break;
    }
  }

  if (strokeColor) {
    switch (shape) {
      case MarkerShape.CIRCLE:
        withVisualPaint(paint => {
          paint.style = PaintStyle.STROKE;
          paint.color = strokeColor;
          visual.drawCircle(p, radius, paint);
        });
        break;
      case MarkerShape.SQUARE:
        visual.drawRect(rect(p.x - radius, p.y - radius, p.x + radius, p.y + radius), {
          stroke: { color: strokeColor, width: 1 }
        });
        break;
    }
  }
}

export function drawLineSegment(
  visual: Visual,
  lineSegment: LineSegment,
  color: Color,
  strokeWidth = 1
) {
  withVisualPaint(paint => {
    paint.style = PaintStyle.STROKE;
    paint.color = color;
    paint.strokeWidth = strokeWidth;
    visual.drawLine(lineSegment, paint);
  });
}
// `deletePath()` must be called after the path is no longer needed.

export function toCKPath(path: Path): CKPath {
  const ckPath = makePath();
  ckPath.moveTo(path.start.x, path.start.y);
  for (const segment of path.segments) {
    if (segment instanceof LineSegment) {
      ckPath.lineTo(segment.p2.x, segment.p2.y);
    } else if (segment instanceof BezierCurve) {
      ckPath.cubicTo(
        segment.points[1].x,
        segment.points[1].y,
        segment.points[2].x,
        segment.points[2].y,
        segment.points[3].x,
        segment.points[3].y
      );
    } else if (segment instanceof Ellipse) {
      const e = segment;
      const rx = e.radii.x;
      const ry = e.radii.y;
      const xAxisRotate = e.xRotation.degrees;
      const largeArc = e.largeArc;
      const sweepFlag = e.isCounterclockwise;
      const x = e.end.x;
      const y = e.end.y;
      ckPath.arcToRotated(rx, ry, xAxisRotate, !largeArc, !sweepFlag, x, y);
    } else {
      throw new Error(`Unsupported segment type: ${segment.constructor.name}`);
    }
  }
  return ckPath;
}
